/**
 * Push client — helpers to manage the current device's push subscription.
 *
 * These functions talk to the server via plain fetch using the Bearer token
 * from Redux state (via the `getAuthToken` accessor passed in) so we don't
 * couple this module to any particular RTK Query hook — it needs to be
 * callable from non-component code like useLogout().
 *
 * Two channels behind one face: in a browser or the Home Screen web app this
 * is the service-worker subscription; inside the iOS store app, where no
 * service worker exists, every call delegates to native/push.js (the APNs
 * device token). Callers — the Settings toggle, NotificationGate, logout,
 * the login-time reconnect — never need to know which.
 */
import {
  isNativeApp,
  isNativePushSupported,
  getCachedNativePermission,
  getNativePushPermission,
  isNativePushRegistered,
  probeNativePushStatus,
  registerNativePush,
  unregisterNativePush,
} from "../native/push";

import { PushPermissionError } from "./pushErrors";

export { PushPermissionError, isNativePushSupported };

const API_BASE = process.env.REACT_APP_BASE_URL || "";

const buildUrl = (path) => {
  if (!API_BASE) return path;
  return API_BASE.endsWith("/") ? `${API_BASE.slice(0, -1)}${path}` : `${API_BASE}${path}`;
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported() {
  if (typeof window === "undefined") return false;
  // The iOS store app has its own channel (APNs via native/push.js). Any
  // other native build (Android) has neither that nor a service worker.
  if (isNativePushSupported()) return true;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports a desktop ("Macintosh") UA but is touch-capable.
  const iPadDesktop =
    /Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/i.test(ua) || iPadDesktop;
}

/**
 * An iPhone specifically — the store app is iPhone-only, so the "get it on
 * the App Store" paths apply here and not on an iPad (which keeps the Home
 * Screen web app). True inside the store app too: callers that must exclude
 * it check isNativeApp() / isStandaloneDisplay() themselves.
 */
export function isIphone() {
  if (typeof navigator === "undefined") return false;
  return /iPhone/i.test(navigator.userAgent || "");
}

/**
 * The retiring iPhone Home Screen web app: an iPhone, launched from the Home
 * Screen icon, and not the store app. These people are moved to the App
 * Store (pwa/MoveToAppStoreGate.jsx).
 */
export function isIphoneHomeScreenApp() {
  return isIphone() && !isNativeApp() && isStandaloneDisplay();
}

/**
 * Which browser is rendering on iOS, so the install guide can point at the
 * right control. Safari ≤18 keeps a bottom-centre Share button; iOS 26 Safari
 * hides it in a bottom-right "•••" menu; Chrome/Edge expose theirs from a
 * top-right menu. Returns null off iOS.
 *   "safari" | "chrome" | "firefox" | "edge" | null
 */
export function getIosBrowser() {
  if (!isIosSafari()) return null;
  const ua = navigator.userAgent || "";
  if (/CriOS/i.test(ua)) return "chrome";
  if (/EdgiOS/i.test(ua)) return "edge";
  if (/FxiOS/i.test(ua)) return "firefox";
  return "safari";
}

/**
 * iOS / iPadOS major version parsed from the UA (e.g. 18, 26), or null when it
 * can't be determined. iPhone/iPad report "...OS 26_0 like Mac OS X"; the
 * iPadOS desktop-class UA reports a Mac "Version/" we can't map to an iOS
 * number, so that path returns null and callers fall back to defaults.
 */
export function getIosVersion() {
  if (!isIosSafari()) return null;
  const ua = navigator.userAgent || "";
  const m = ua.match(/OS (\d+)_/i);
  return m ? parseInt(m[1], 10) : null;
}

export function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  // The store app IS the installed app: the "add to Home Screen first" guide
  // must never show inside it.
  if (isNativeApp()) return true;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  return Boolean((mq && mq.matches) || window.navigator.standalone);
}

/**
 * Synchronous permission state. In the browser this is the truth; in the
 * iOS app it is the last answer the plugin gave (warmed at boot and after
 * every ask), good enough to render from. Decisions go through
 * getPermissionAsync().
 */
export function getPermission() {
  if (isNativePushSupported()) return getCachedNativePermission();
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission; // "default" | "granted" | "denied"
}

/** The permission state from the source of truth on every platform. */
export async function getPermissionAsync() {
  if (isNativePushSupported()) return getNativePushPermission();
  return getPermission();
}

async function fetchPublicKey() {
  const res = await fetch(buildUrl("/push/public-key"));
  if (!res.ok) throw new Error("Failed to fetch VAPID public key");
  const body = await res.json();
  if (!body.publicKey) throw new Error("Server returned no public key");
  return body.publicKey;
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  // Wait for the SW to actually be active so pushManager is usable.
  await navigator.serviceWorker.ready;
  return navigator.serviceWorker.getRegistration("/");
}

export async function getCurrentSubscription() {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function isCurrentlySubscribed() {
  if (isNativePushSupported()) return isNativePushRegistered();
  if (!isPushSupported()) return false;
  if (getPermission() !== "granted") return false;
  const sub = await getCurrentSubscription();
  return Boolean(sub);
}

/**
 * Everything the Settings card renders from, in one bounded read:
 * { permission, registered, stalledAt }. On the web `stalledAt` is always
 * null; inside the iOS app it names the native step that never answered
 * (native/push.js probeNativePushStatus) so the card can say so instead of
 * spinning forever.
 */
export async function getPushStatus() {
  if (isNativePushSupported()) return probeNativePushStatus();
  return {
    permission: getPermission(),
    registered: await isCurrentlySubscribed(),
    stalledAt: null,
  };
}

/**
 * Subscribe the current device to push and tell the server. Must be invoked
 * from a user gesture (click handler): both the browser and iOS only show
 * their permission dialog from one.
 *
 * @param {string} authToken - Bearer token for the current session.
 */
export async function subscribeToPush(authToken) {
  if (isNativePushSupported()) {
    await registerNativePush(authToken);
    return null; // no PushSubscription JSON on this channel
  }
  if (!isPushSupported()) {
    throw new Error("Push is not supported in this browser");
  }
  if (!authToken) {
    throw new Error("Not authenticated");
  }

  const permission = getPermission();
  if (permission === "denied") {
    throw new PushPermissionError("denied");
  }

  // Requesting permission is a no-op if already granted.
  const next = await Notification.requestPermission();
  if (next !== "granted") {
    throw new PushPermissionError(next);
  }

  const reg = await getRegistration();
  if (!reg) throw new Error("Service worker not registered");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const publicKey = await fetchPublicKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const res = await fetch(buildUrl("/push/subscribe"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      subscription: sub.toJSON(),
      userAgent: navigator.userAgent,
    }),
  });

  if (!res.ok) {
    throw new Error(`Subscribe failed: ${res.status}`);
  }

  return sub.toJSON();
}

/**
 * Unsubscribe the current browser from push. Hits the server FIRST (so auth
 * is still valid) then unsubscribes the browser. Swallow-friendly on errors:
 * callers in logout paths just catch and ignore.
 *
 * @param {string|null} authToken - Bearer token, or null if already cleared.
 */
export async function unsubscribeFromPush(authToken) {
  if (isNativePushSupported()) {
    await unregisterNativePush(authToken);
    return;
  }
  if (!isPushSupported()) return;

  const sub = await getCurrentSubscription();
  if (!sub) return;

  try {
    if (authToken) {
      await fetch(buildUrl("/push/subscribe"), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
    }
  } catch (_) {
    /* best-effort */
  }

  try {
    await sub.unsubscribe();
  } catch (_) {
    /* best-effort */
  }
}
